/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../firebase";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { FaEdit, FaTrash, FaBell } from "react-icons/fa";
import ReminderModal from "../components/appointments/ReminderModal";
import { hasPermission, ROLES, FEATURES } from "../lib/rolesConfig";

// Helper to combine date and time strings into a Date object
const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  return new Date(`${dateStr}T${timeStr}`);
};

// Helper to format Firestore Timestamp for display (e.g., date and time separately or combined)
// (formatAppointmentDateTime removed – replaced by formatTimeRange)

// Helper to format a start & end timestamp into date or time range strings
const formatTimeRange = (startTs, endTs, part) => {
  if (!startTs || !startTs.toDate) return "N/A";
  const start = startTs.toDate();
  const end = endTs && endTs.toDate ? endTs.toDate() : new Date(start.getTime() + 60*60*1000);
  if (part === "date") return start.toLocaleDateString();
  if (part === "timeRange") {
    const s = start.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
    const e = end.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" });
    return `${s} – ${e}`;
  }
  return `${start.toLocaleString()} – ${end.toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}`;
};

// Helper to format Firestore Timestamp for input fields
const formatTimestampForDateInput = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return "";
  const date = timestamp.toDate();
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatTimestampForTimeInput = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return "";
  const date = timestamp.toDate();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getDynamicStatus = (dateTime, manualStatus) => {
  const statusLower = (manualStatus || "").toLowerCase();
  if (statusLower === "cancelled") return { text: "Cancelled", color: "red" };
  if (statusLower === "completed") return { text: "Completed", color: "green" };
  if (statusLower === "rescheduled") return { text: "Rescheduled", color: "purple" };
  if (!dateTime || !dateTime.toDate) {
    return { text: "Date TBD", color: "gray" };
  }
  const now = new Date();
  const apptDate = dateTime.toDate();
  const oneHourAfter = new Date(apptDate.getTime() + 60 * 60 * 1000);

  if (now > oneHourAfter) {
    return { text: "Completed", color: "green" };
  }
  if (now > apptDate && now <= oneHourAfter) {
    return { text: "In Progress", color: "yellow" };
  }
  if (now < apptDate) {
    const diffHours = (apptDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (diffHours <= 24) {
      return { text: "Upcoming (Soon)", color: "orange" };
    }
    return { text: "Upcoming", color: "blue" };
  }
  return { text: "Unknown", color: "gray" }; // Should not happen
};

const AppointmentsPage = () => {
  const { currentUser, activeCareProfile, userRole } = useAuth();
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [appointmentDate, setAppointmentDate] = useState("");
  const [startTimeStr, setStartTimeStr] = useState("");
  const [endTimeStr, setEndTimeStr] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [reason, setReason] = useState("");
  const [locationField, setLocationField] = useState("");
  const [status, setStatus] = useState("scheduled"); // Default status
  const [appointmentNotes, setAppointmentNotes] = useState("");

  // Edit state
  const [isEditingAppointment, setIsEditingAppointment] = useState(false);
  const [currentAppointmentId, setCurrentAppointmentId] = useState(null);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [selectedAppointmentForReminder, setSelectedAppointmentForReminder] =
    useState(null);
  const cardRefs = useRef({});

  // Load More State
  const [visibleAppointmentsCount, setVisibleAppointmentsCount] = useState(5);

  const canWrite =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.APPOINTMENTS,
          "write",
        )
      : false;
  const canRead =
    activeCareProfile && userRole
      ? hasPermission(
          userRole[activeCareProfile.id] || ROLES.OWNER,
          FEATURES.APPOINTMENTS,
          "read",
        )
      : false;

  const fetchAppointments = useCallback(async () => {
    if (!canRead || !activeCareProfile) {
      setAppointmentsList([]);
      setIsLoadingAppointments(false);
      return;
    }
    setIsLoadingAppointments(true);
    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/appointments/${activeCareProfile.id}`,
        {
          headers: { Authorization: `Bearer ${idToken}` },
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch appointments");
      }
      const fetchedAppointments = await response.json();

      const toTimestamp = (value) => {
        if (!value) return null;
        if (typeof value === "string") return Timestamp.fromDate(new Date(value));
        if (value._seconds !== undefined && value._nanoseconds !== undefined) {
          return new Timestamp(value._seconds, value._nanoseconds);
        }
        if (value.seconds !== undefined && value.nanoseconds !== undefined) {
          return new Timestamp(value.seconds, value.nanoseconds);
        }
        return null;
      };

      const formattedAppointments = fetchedAppointments.map((appt) => {
        const startTs = toTimestamp(appt.startTime) || toTimestamp(appt.dateTime);
        const endTsRaw = toTimestamp(appt.endTime);
        const endTs = endTsRaw || (startTs ? Timestamp.fromMillis(startTs.toMillis() + 60 * 60 * 1000) : null);
        return {
          ...appt,
          startTime: startTs,
          endTime: endTs,
          createdAt: toTimestamp(appt.createdAt),
          lastModifiedAt: toTimestamp(appt.lastModifiedAt),
        };
      }).sort((a, b) => {
        if (!a.startTime || !b.startTime) return 0;
        return b.startTime.toMillis() - a.startTime.toMillis();
      });

      setAppointmentsList(formattedAppointments);
    } catch (error) {
      console.error("Error fetching appointments: ", error);
      window.alert(`Failed to fetch appointments: ${error.message}`);
    } finally {
      setIsLoadingAppointments(false);
    }
  }, [currentUser, activeCareProfile, canRead]);

  useEffect(() => {
    if ("Notification" in window) {
      console.log(
        "Current notification permission status:",
        Notification.permission,
      );
      if (
        Notification.permission !== "granted" &&
        Notification.permission !== "denied"
      ) {
        Notification.requestPermission().then((permission) => {
          console.log(
            "Notification permission status after request:",
            permission,
          );
        });
      }
    } else {
      console.log("This browser does not support desktop notification");
    }
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleLoadMore = () => {
    setVisibleAppointmentsCount((prevCount) => prevCount + 5);
  };

  const resetForm = () => {
    setAppointmentDate("");
    setStartTimeStr("");
    setEndTimeStr("");
    setDoctorName("");
    setReason("");
    setLocationField("");
    setStatus("scheduled");
    setAppointmentNotes("");
    setIsEditingAppointment(false);
    setCurrentAppointmentId(null);
    setShowForm(false);
  };

  const handleCancelEdit = () => {
    const idToScrollTo = currentAppointmentId;
    resetForm();
    setShowForm(false);
    if (idToScrollTo && cardRefs.current[idToScrollTo]) {
      setTimeout(() => {
        cardRefs.current[idToScrollTo].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
    }
  };

  const handleEditAppointment = (appt) => {
    if (!canWrite)
      return alert("You don't have permission to edit appointments.");
    setIsEditingAppointment(true);
    setCurrentAppointmentId(appt.id);
    setAppointmentDate(formatTimestampForDateInput(appt.startTime || appt.dateTime));
    const startSrc = appt.startTime || appt.dateTime;
    const endSrc = appt.endTime ? appt.endTime : (startSrc ? Timestamp.fromMillis(startSrc.toMillis() + 60*60*1000) : null);
    setStartTimeStr(formatTimestampForTimeInput(startSrc));
    setEndTimeStr(formatTimestampForTimeInput(endSrc));
    setDoctorName(appt.doctorName || "");
    setReason(appt.title || appt.reason || "");
    setLocationField(appt.location || "");
    setStatus(appt.status || "scheduled");
    setAppointmentNotes(appt.notes || "");
    setShowForm(true);
    setTimeout(() => {
      const formElement = document.getElementById("appointment-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  const handleDeleteAppointment = async (id) => {
    if (!canWrite)
      return alert("You don't have permission to delete appointments.");
    if (!currentUser) {
      window.alert("User not authenticated. Cannot delete appointment.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(`${API_BASE_URL}/appointments/delete`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            careProfileId: activeCareProfile.id,
            appointmentId: id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Server error");
        }

        window.alert("Appointment deleted successfully!");
        if (id === currentAppointmentId) {
          resetForm();
          setShowForm(false);
        }
        fetchAppointments();
      } catch (error) {
        console.error("Error deleting appointment: ", error);
        window.alert(`Failed to delete appointment. ${error.message}`);
      }
    }
  };

  const handleSubmitAppointment = async (e) => {
    e.preventDefault();
    if (!canWrite)
      return alert("You don't have permission to save appointments.");
    if (!currentUser || !activeCareProfile) {
      window.alert(
        "User not authenticated or no active care profile selected. Cannot save appointment.",
      );
      return;
    }
    if (!appointmentDate || !startTimeStr || !endTimeStr || !doctorName || !reason || !locationField) {
      window.alert("Please fill in Date, Start/End Time, Title, Location, and Doctor/Specialist.");
      return;
    }
    const startISO = combineDateTime(appointmentDate, startTimeStr)?.toISOString();
    const endISO = combineDateTime(appointmentDate, endTimeStr)?.toISOString();
    if (!startISO || !endISO) {
      window.alert("Invalid date or time format.");
      return;
    }

    const appointmentData = {
      startTime: startISO,
      endTime: endISO,
      doctorName,
      title: reason,
      location: locationField,
      status: status ? status.toLowerCase() : undefined,
      notes: appointmentNotes,
    };

    try {
      const idToken = await currentUser.getIdToken();
      let url = "";
      let body = {};

      if (isEditingAppointment && currentAppointmentId) {
        url = `${API_BASE_URL}/appointments/update`;
        body = {
          careProfileId: activeCareProfile.id,
          appointmentId: currentAppointmentId,
          appointmentData,
        };
      } else {
        url = `${API_BASE_URL}/appointments/create`;
        body = {
          careProfileId: activeCareProfile.id,
          appointmentData,
        };
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Server error");
      }

      window.alert(
        `Appointment ${isEditingAppointment ? "updated" : "created"} successfully!`,
      );
      resetForm();
      fetchAppointments();
    } catch (error) {
      console.error(
        `Error ${isEditingAppointment ? "updating" : "creating"} appointment: `,
        error,
      );
      window.alert(
        `Failed to ${isEditingAppointment ? "update" : "create"} appointment. ${error.message}`,
      );
    }
  };

  const handleOpenReminderModal = (appointment) => {
    if (!("Notification" in window)) {
      alert("This browser does not support desktop notifications.");
      return;
    }
    if (Notification.permission === "denied") {
      alert(
        "Notifications are blocked. Please enable them in your browser settings to set reminders.",
      );
      return;
    }
    setSelectedAppointmentForReminder(appointment);
    setIsReminderModalOpen(true);
  };

  const handleCloseReminderModal = () => {
    setSelectedAppointmentForReminder(null);
    setIsReminderModalOpen(false);
  };

  const handleSetReminder = (reminderMinutes) => {
    console.log(
      `Attempting to set a reminder for ${reminderMinutes} minutes before the appointment.`,
    );

    if (!("Notification" in window) || Notification.permission !== "granted") {
      alert(
        "Please enable notifications to set a reminder. You may need to grant permission in your browser settings.",
      );
      console.error("Notification permission not granted.");
      return;
    }

    const appointmentTime = selectedAppointmentForReminder.dateTime.toDate();
    const reminderTime = new Date(
      appointmentTime.getTime() - reminderMinutes * 60 * 1000,
    );
    const now = new Date();

    console.log("Appointment time:", appointmentTime.toLocaleString());
    console.log("Reminder time:", reminderTime.toLocaleString());
    console.log("Current time:", now.toLocaleString());

    if (reminderTime > now) {
      const timeout = reminderTime.getTime() - now.getTime();
      console.log(`Setting a timeout for ${timeout / 1000} seconds from now.`);

      setTimeout(() => {
        console.log("Firing notification now!");
        // Play alarm sound
        const alarm = new Audio("/sounds/Alarm04.wav");
        alarm
          .play()
          .catch((error) => console.error("Error playing alarm sound:", error));

        new Notification(
          `Reminder: Appointment with ${selectedAppointmentForReminder.doctorName}`,
          {
            body: `Your appointment for "${selectedAppointmentForReminder.reason}" is in ${reminderMinutes} minutes.`,
            icon: "/logo.png",
          },
        );
      }, timeout);
      alert(
        `Reminder set for your appointment with ${selectedAppointmentForReminder.doctorName}.`,
      );
    } else {
      alert(
        "This appointment has already passed or is less than the reminder time away.",
      );
      console.warn("Reminder time is in the past. No notification was set.");
    }
    handleCloseReminderModal();
  };

  return (
    <>
      <div className="container mx-auto p-4 sm:p-6">
        <div className="flex flex-wrap justify-between items-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-blue-600 mb-2 sm:mb-0">
            Appointments
          </h1>
          {canWrite && (
            <button
              onClick={() => {
                if (showForm || isEditingAppointment) {
                  handleCancelEdit();
                } else {
                  resetForm(); // Clear form for new entry
                  setShowForm(true);
                  setTimeout(() => {
                    const formElement =
                      document.getElementById("appointment-form");
                    if (formElement) {
                      formElement.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }, 100);
                }
              }}
              className={`w-full sm:w-auto px-4 py-2 sm:px-6 text-white font-semibold rounded-lg shadow-md transition duration-300 ease-in-out ${
                isEditingAppointment || showForm
                  ? "bg-red-500 hover:bg-red-600"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {showForm || isEditingAppointment ? "Cancel" : "Schedule New"}
            </button>
          )}
        </div>

        {showForm && (
          <div
            id="appointment-form"
            className="mb-8 bg-white p-6 rounded-lg shadow-lg border-l-4 border-blue-500"
          >
            <h3 className="text-2xl font-semibold text-gray-700 mb-6">
              {isEditingAppointment
                ? "Edit Appointment"
                : "Schedule a New Appointment"}
            </h3>
            <form
              onSubmit={handleSubmitAppointment}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              <div>
                <label
                  htmlFor="appointmentDate"
                  className="block text-sm font-medium text-gray-600 mb-1"
                >
                  Date
                </label>
                <input
                  type="date"
                  id="appointmentDate"
                  value={appointmentDate}
                  onChange={(e) => setAppointmentDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-600 mb-1">Start Time</label>
                <input type="time" id="startTime" value={startTimeStr} onChange={(e)=>setStartTimeStr(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg" required />
              </div>
              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-600 mb-1">End Time</label>
                <input type="time" id="endTime" value={endTimeStr} onChange={(e)=>setEndTimeStr(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg" required />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="reason" className="block text-sm font-medium text-gray-600 mb-1">Title</label>
                <input type="text" id="reason" value={reason} onChange={(e)=>setReason(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="e.g., Annual Check-up" required />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="location" className="block text-sm font-medium text-gray-600 mb-1">Location</label>
                <input type="text" id="location" value={locationField} onChange={(e)=>setLocationField(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="e.g., MMC" required />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="doctorName" className="block text-sm font-medium text-gray-600 mb-1">Doctor / Specialist</label>
                <input type="text" id="doctorName" value={doctorName} onChange={(e)=>setDoctorName(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg" placeholder="e.g., Dr. Smith" required />
              </div>
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-600 mb-1"
                >
                  Status
                </label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                >
                  <option value="scheduled">Upcoming</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="appointmentNotes"
                  className="block text-sm font-medium text-gray-600 mb-1"
                >
                  Notes
                </label>
                <textarea
                  id="appointmentNotes"
                  rows="3"
                  value={appointmentNotes}
                  onChange={(e) => setAppointmentNotes(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  placeholder="e.g., Remember to bring test results"
                ></textarea>
              </div>
              <div className="md:col-span-2 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-6 py-2 text-gray-700 bg-gray-200 font-semibold rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 text-white bg-blue-600 font-semibold rounded-lg hover:bg-blue-700"
                >
                  {isEditingAppointment
                    ? "Update Appointment"
                    : "Save Appointment"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border-l-4 border-blue-500">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">History</h2>
          {isLoadingAppointments ? (
            <p className="text-center text-gray-500 py-4">
              Loading appointments...
            </p>
          ) : appointmentsList.length > 0 ? (
            <div className="space-y-4 lg:space-y-2">
              {appointmentsList
                .slice(0, visibleAppointmentsCount)
                .map((appt) => {
                  const statusInfo = getDynamicStatus(
                    appt.startTime || appt.dateTime,
                    appt.status,
                  );
                  return (
                    <div
                      key={appt.id}
                      ref={(el) => (cardRefs.current[appt.id] = el)}
                      className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200 relative"
                    >
                      {/* --- Unified Card (all breakpoints) --- */}
                      <div className="block">
                        {/* Action icons for TABLET view (top-right) */}
                        <div className="hidden sm:flex items-center justify-end gap-2 absolute top-4 right-4">
                          <button
                            onClick={() => handleOpenReminderModal(appt)}
                            className="p-2 text-gray-500 hover:text-blue-600"
                            title="Set Reminder"
                          >
                            <FaBell />
                          </button>
                          {canWrite && (
                            <>
                              <button
                                onClick={() => handleEditAppointment(appt)}
                                className="p-2 text-gray-500 hover:text-green-600"
                                title="Edit"
                              >
                                <FaEdit />
                              </button>
                              <button
                                onClick={() => handleDeleteAppointment(appt.id)}
                                className="p-2 text-gray-500 hover:text-red-600"
                                title="Delete"
                              >
                                <FaTrash />
                              </button>
                            </>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div className="col-span-2">
                            <p className="text-sm font-semibold text-gray-500">
                              Date & Time
                            </p>
                            <p className="text-gray-800 font-medium">
                              {formatTimeRange(appt.startTime, appt.endTime, "date")} {" "}at{" "}
                              {formatTimeRange(appt.startTime, appt.endTime, "timeRange")}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-500">Title</p>
                            <p className="text-gray-800">{appt.title || appt.reason}</p>
                          </div>
                          {appt.location && (
                            <div>
                              <p className="text-sm font-semibold text-gray-500">Location</p>
                              <p className="text-gray-800">{appt.location}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-gray-500">Doctor/Specialist</p>
                            <p className="text-gray-800">{appt.doctorName}</p>
                          </div>
                          {appt.notes && (
                            <div className="col-span-2">
                              <p className="text-sm font-semibold text-gray-500">
                                Notes
                              </p>
                              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                {appt.notes}
                              </p>
                            </div>
                          )}
                          {/* Combined Status and action buttons */}
                          <div className="col-span-2 mt-2 flex justify-between items-end">
                            <div>
                              <p className="text-sm font-semibold text-gray-500">
                                Status
                              </p>
                              <span
                                className={`px-2 py-1 text-xs font-semibold text-white rounded-full bg-${statusInfo.color}-500`}
                              >
                                {statusInfo.text}
                              </span>
                            </div>
                            {/* Action icons for MOBILE view (inline with Status) */}
                            <div className="flex items-center sm:hidden">
                              <button
                                onClick={() => handleOpenReminderModal(appt)}
                                className="p-2 text-gray-500 hover:text-blue-600"
                                title="Set Reminder"
                              >
                                <FaBell />
                              </button>
                              {canWrite && (
                                <>
                                  <button
                                    onClick={() => handleEditAppointment(appt)}
                                    className="p-2 text-gray-500 hover:text-green-600"
                                    title="Edit"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteAppointment(appt.id)
                                    }
                                    className="p-2 text-gray-500 hover:text-red-600"
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {appt.lastModifiedBy &&
                            appt.lastModifiedBy.uid !==
                              activeCareProfile.userId && (
                              <div className="col-span-2 mt-2 pt-2 border-t border-gray-200">
                                <p className="text-xs text-gray-500 italic">
                                  Last updated by: {appt.lastModifiedBy.name}
                                </p>
                              </div>
                            )}
                        </div>
                      </div>
                      {/* Tablet/Desktop grids removed to keep uniform card layout */}
                      {/* Display Appointment Notes on Desktop */}
                      {appt.notes && (
                        <div className="hidden lg:block mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-600 font-semibold">
                            Notes:
                          </p>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {appt.notes}
                          </p>
                        </div>
                      )}
                      {/* This needs to be inside the main item div, but separate from the grid for proper layout */}
                      {appt.lastModifiedBy &&
                        appt.lastModifiedBy.uid !==
                          activeCareProfile.userId && (
                          <div className="hidden lg:block mt-2 pt-2 border-t border-gray-200 text-right">
                            <p className="text-xs text-gray-500 italic">
                              Last updated by: {appt.lastModifiedBy.name}
                            </p>
                          </div>
                        )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">
              No appointments scheduled yet. Click "Schedule New" to add the
              first one.
            </p>
          )}
          {appointmentsList.length > visibleAppointmentsCount && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition"
              >
                Load More
              </button>
            </div>
          )}
        </div>
      </div>
      <ReminderModal
        isOpen={isReminderModalOpen}
        onClose={handleCloseReminderModal}
        onSetReminder={handleSetReminder}
        appointment={selectedAppointmentForReminder}
      />
    </>
  );
};

export default AppointmentsPage;
